// One archetype card: glyph, name, tier dots, next-tier unlock, blurb, invest button.

import { Button } from '@aod/ui/components/ui/button';
import { Card, CardContent } from '@aod/ui/components/ui/card';
import { cn } from '@aod/ui/lib/utils';
import { type MessageKey, useT } from '../../i18n/index.js';
import { DotsRow } from './DotsRow.js';
import { type ArchetypeMeta, TIER_CAP } from './archetypes.js';
import type { DoctrineGateReason } from './gating.js';

interface DoctrineCardProps {
  readonly meta: ArchetypeMeta;
  readonly points: number;
  readonly canInvest: boolean;
  readonly gateReason: DoctrineGateReason;
  readonly onInvest: () => void;
}

export function DoctrineCard({ meta, points, canInvest, gateReason, onInvest }: DoctrineCardProps) {
  const t = useT();
  const { Icon, tone, id, tierUnlocks } = meta;
  const name = t(`archetype.${id}`);
  const blurb = t(`archetype.${id}.blurb`);
  // Reference the raw `--doctrine-*` tokens defined on :root in globals.css.
  // Tailwind's `@theme inline` exposes the same colors under `--color-*` for
  // utility classes (e.g. `bg-doctrine-industry`), but those `--color-*` vars
  // are NOT published at :root — so `var(--color-doctrine-${tone})` resolves
  // to nothing in inline styles, leaving the button background transparent
  // and the WCAG contrast ratio at 1.0 (audit finding 2026-07-17).
  const accentColor = `var(--${tone})`;
  const tinted = points > 0;
  // `tierUnlocks[points]` is the unlock the player reaches with their next
  // point. Hidden at cap because there is no "next tier" and the dot row
  // already conveys maxed-out state. Bounds-guarded for short arrays. The
  // catalog's English copy is the source; the dictionary localises it.
  const nextUnlock =
    tierUnlocks && points < TIER_CAP && points < tierUnlocks.length
      ? t(`archetype.${id}.tier${points + 1}` as MessageKey)
      : null;

  return (
    <Card
      className={cn(
        'gap-2 border-border/60 py-2.5 transition-colors',
        tinted && 'border-(--accent-color)/60',
      )}
      style={{ '--accent-color': accentColor } as React.CSSProperties}
    >
      <CardContent className="flex items-center gap-3 px-3">
        <IconBadge Icon={Icon} accentColor={accentColor} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="font-medium leading-none">{name}</span>
            <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
              {points}/{TIER_CAP}
            </span>
          </div>
          <DotsRow archetypeId={id} points={points} accentColor={accentColor} />
          {nextUnlock && (
            <p
              className="text-[11px] leading-snug text-foreground/85"
              data-testid="doctrine-next-unlock"
            >
              <span className="text-muted-foreground">{t('doctrine.next')}</span>
              {nextUnlock}
            </p>
          )}
          <p className="text-[11px] leading-snug text-muted-foreground">{blurb}</p>
          {!canInvest && <GateHint reason={gateReason} />}
        </div>
        <InvestButton
          name={name}
          canInvest={canInvest}
          accentColor={accentColor}
          onInvest={onInvest}
        />
      </CardContent>
    </Card>
  );
}

function GateHint({ reason }: { reason: DoctrineGateReason }) {
  const t = useT();
  if (reason === null) return null;
  const label = reason === 'at_cap' ? t('doctrine.atCap') : t('doctrine.noPoints');
  return (
    <span
      className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80"
      aria-live="polite"
    >
      {label}
    </span>
  );
}

function IconBadge({
  Icon,
  accentColor,
}: {
  Icon: ArchetypeMeta['Icon'];
  accentColor: string;
}) {
  return (
    <div
      className="flex size-9 items-center justify-center rounded-md"
      style={{
        background: `color-mix(in oklch, ${accentColor} 14%, transparent)`,
        color: accentColor,
      }}
      aria-hidden
    >
      <Icon className="size-4" />
    </div>
  );
}

interface InvestButtonProps {
  readonly name: string;
  readonly canInvest: boolean;
  readonly accentColor: string;
  readonly onInvest: () => void;
}

function InvestButton({ name, canInvest, accentColor, onInvest }: InvestButtonProps) {
  const t = useT();
  return (
    <Button
      type="button"
      size="sm"
      variant={canInvest ? 'default' : 'secondary'}
      disabled={!canInvest}
      onClick={onInvest}
      aria-label={t('doctrine.investLabel', { name })}
      className={cn('h-7 px-2.5 text-[11px] tracking-wide uppercase', canInvest && 'shadow-sm')}
      style={canInvest ? { background: accentColor, color: 'oklch(0.135 0.01 264)' } : undefined}
    >
      {t('doctrine.invest')}
    </Button>
  );
}
